// Geofence helpers for the admin polygon tool.
//
// Shapes live in the `schemas` table as a single record (type 'geofences',
// name 'sfo-geofences'), whose `schema` array holds one object per polygon:
//
//   { appId, name, planId, color, coords: [[lat,lng], ...], notes, status }
//
// `planId` scopes a shape to a level/floor, so the same lat/lng can belong to a
// different zone on each level -- a plot only ever matches shapes on its own
// floor. `appId` is the stable shape ID we write back onto each plot.

export const GEOFENCE_TYPE = 'geofences';
export const GEOFENCE_NAME = 'sfo-geofences';

// picked to stay legible at 40% basemap opacity
export const geofenceColors = [
  '#F7A600', '#577590', '#4D908E', '#F3722B', '#5E548E',
  '#00AB52', '#ED1C24', '#8F3257', '#00738B', '#748CAB',
];

// Ray casting. Treats lng as x and lat as y; at SFO's scale the degree
// distortion doesn't matter because containment is topological, not metric.
export const pointInPolygon = (lat, lng, coords) => {
  if(!Array.isArray(coords) || coords.length < 3) return false;

  let inside = false;
  for(let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
    const yi = coords[i]?.[0], xi = coords[i]?.[1];
    const yj = coords[j]?.[0], xj = coords[j]?.[1];
    if(yi == null || xi == null || yj == null || xj == null) continue;

    // does a horizontal ray from the point cross this edge?
    // the first half also guarantees yj !== yi, so the divide is safe
    const crosses = ((yi > lat) !== (yj > lat)) &&
      (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
    if(crosses) inside = !inside;
  }
  return inside;
}

// Is `inner` wholly inside `outer`? Approximated by testing every vertex, which
// is exact for convex shapes and close enough for the concave ones drawn here.
// Used to place a newly drawn sub-zone above the zone that contains it.
export const polygonContains = (outer, inner) => {
  if(!Array.isArray(outer) || outer.length < 3) return false;
  if(!Array.isArray(inner) || !inner.length) return false;
  return inner.every(coord => pointInPolygon(coord?.[0], coord?.[1], outer));
}

export const activeShapes = (shapes) =>
  (Array.isArray(shapes) ? shapes : []).filter(x => x.status !== 'D');

// Array order is precedence, so this must preserve it.
export const shapesForPlan = (shapes, planId) =>
  activeShapes(shapes).filter(x => Number(x.planId) === Number(planId));

// Match one plot to a shape: same floor, and its lat/lng inside the polygon.
// Where zones overlap the FIRST match in list order wins, so the manual sort in
// the admin tool is what decides -- no hidden heuristic to reason around.
export const findGeofence = (plot, shapes) => {
  const lat = Number(plot?.styling?.lat);
  const lng = Number(plot?.styling?.lng);
  if(!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return shapesForPlan(shapes, plot?.planId)
    .find(shape => pointInPolygon(lat, lng, shape.coords)) ?? null;
}

// Add a new zone to the list. It goes directly above the first zone on its own
// floor that fully contains it, so drawing a sub-zone inside a bigger one just
// works; anything else is appended. The admin can drag it elsewhere after.
export const insertShape = (shapes, shape) => {
  const next = [...(shapes ?? [])];
  const at = next.findIndex(x =>
    x.status !== 'D' &&
    Number(x.planId) === Number(shape.planId) &&
    polygonContains(x.coords, shape.coords));

  if(at === -1) next.push(shape);
  else next.splice(at, 0, shape);
  return next;
}

// Move a zone within one floor's ordering, leaving every other floor's entries
// exactly where they sit in the shared array.
export const reorderPlanShapes = (shapes, planId, from, to) => {
  const all = Array.isArray(shapes) ? shapes : [];

  const slots = [];
  all.forEach((shape, idx) => {
    if(shape.status !== 'D' && Number(shape.planId) === Number(planId)) slots.push(idx);
  });

  const ordered = slots.map(idx => all[idx]);
  if(from < 0 || to < 0 || from >= ordered.length || to >= ordered.length || from === to) return all;

  const [moved] = ordered.splice(from, 1);
  ordered.splice(to, 0, moved);

  const next = [...all];
  slots.forEach((slot, i) => { next[slot] = ordered[i]; });
  return next;
}

// The zone a plot is currently assigned to, for display. Returns the whole
// shape so callers can use its colour as well as its name.
//
// Returns null for a plot that was never matched AND for one carrying an ID
// whose zone has since been deleted -- from a reader's point of view both mean
// "no zone", and showing a dangling ID would be worse than showing nothing.
export const geofenceFor = (plot, shapes) => {
  const appId = plot?.styling?.geofenceId;
  if(!appId) return null;
  return activeShapes(shapes).find(x => x.appId === appId) ?? null;
}

// Stamp onto `styling` the zone its GIS coordinates fall in. Mutates in place,
// because the plot helpers it serves already mutate styling directly.
//
// Deliberately a no-op unless the caller passed a non-empty geofences array:
// callers that know nothing about geofencing, or that run before any zones have
// been drawn, must leave an existing geofenceId alone rather than silently
// wiping it. When zones ARE passed and the point matches none of them, the key
// is removed -- that's a real "no longer in any zone" answer, not an absence.
export const stampGeofence = ({ styling, planId, geofences }) => {
  if(!Array.isArray(geofences) || !geofences.length) return styling;
  if(styling?.lat === undefined || styling?.lng === undefined) return styling;

  const zone = findGeofence({ planId, styling }, geofences);
  if(zone) styling.geofenceId = zone.appId;
  else delete styling.geofenceId;

  return styling;
}

// Run every plot through findGeofence. Returns one row per plot so callers can
// report on unmatched/unplaced plots, not just the ones that need writing.
export const assignGeofences = (plots, shapes) => {
  const rows = [];
  for(const plot of (plots ?? [])) {
    const placed = Number.isFinite(Number(plot?.styling?.lat)) && Number.isFinite(Number(plot?.styling?.lng));
    const shape = placed ? findGeofence(plot, shapes) : null;
    const geofenceId = shape ? shape.appId : null;
    const previous = plot?.styling?.geofenceId ?? null;

    rows.push({ plot, shape, geofenceId, placed, changed: geofenceId !== previous });
  }
  return rows;
}

// Build the payload for PATCH /mapping/plots/bulk. `styling` is a single JSON
// blob on the plot, so we have to send it whole -- sending just the geofenceId
// would wipe out lat/lng/rotation.
export const geofenceUpdates = (rows) =>
  (rows ?? []).filter(row => row.changed).map(({ plot, geofenceId }) => {
    const styling = { ...plot.styling };
    if(geofenceId) styling.geofenceId = geofenceId;
    else delete styling.geofenceId;

    return { id: plot.id, appId: plot.appId, phaseId: plot.phaseId, styling };
  });
